import { Organisation } from '@futrli-sdk/organisations';
import { FinancialConfig } from '@futrli-sdk/predict';
import { useErrorHandler } from '@futrli/errors';
import {
  currencySelector,
  findProductSetupById,
  getOrganisationsSettingsUrl,
  hasAllowedGoogleSheetsImports,
  hasFrenchAccountingRules,
  Logger,
  PREDICT_PRODUCT_ID
} from '@futrli/shared-services';
import { LOADER_AREAS, UILoader, UIPromiseTrackerLoader } from '@futrli/ui-components';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { fetchOrganisationById } from '../../core/api/workspace-coordinator';
import { addNormalisedErrorMessage } from '../../flash-list/flash-list-actions';
import { PredictManagement } from '../components/predict-management';
import { IPredictManagementContext, PredictManagementContext } from '../context/predict-management-context';
import { fetchFinancialConfig } from '../../core/api/financial-config';
import { useOnboardingUrlInfo } from '../../forecast/onboarding/utils/hooks';
import { InitialOnboardingLoader } from '../../../../skeleton/src/onboarding/components/initial-onboarding-nav';

export const PredictManagementContainer: FC = () => {
  const handleError = useErrorHandler();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId as string;
  const organisationId = tempOrganisationId as string;
  const [loading, setLoading] = useState<boolean>(false);
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [organisationNotFound, setOrganisationNotFound] = useState<boolean>(false);
  const [financialConfig, setFinancialConfig] = useState<FinancialConfig>();

  const { isOnboardingRoute, onboardingForecastId } = useOnboardingUrlInfo();

  const memoizedAddNormalisedErrorMessage = useMemo(() => addNormalisedErrorMessage(), []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setOrganisationNotFound(false);

      const [orgResult, finConfigResult] = await Promise.allSettled([
        fetchOrganisationById(workspaceId, organisationId),
        fetchFinancialConfig(workspaceId, organisationId)
      ]);

      if (orgResult.status === 'fulfilled') {
        setOrganisation(orgResult.value);
      } else {
        Logger.error(orgResult.reason as string);
      }

      if (finConfigResult.status === 'fulfilled') {
        setFinancialConfig(finConfigResult.value);
      } else {
        Logger.error(finConfigResult.reason as string);
      }

      if (orgResult.status === 'rejected' && finConfigResult.status === 'rejected') {
        setOrganisationNotFound(true);
      }

      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, workspaceId, isOnboardingRoute]);

  const refetchOrganisation = async () => {
    try {
      setOrganisation(null);
      const org = await fetchOrganisationById(workspaceId, organisationId);

      setOrganisation(org);
    } catch (e) {
      handleError(e);
    }
  };

  if (organisationNotFound) {
    return <Navigate to={getOrganisationsSettingsUrl(workspaceId)} replace />;
  }

  if (loading || !organisation) {
    if (isOnboardingRoute || onboardingForecastId) {
      return <InitialOnboardingLoader />;
    }
    return <UILoader isFull={true} />;
  }

  const getPredictContext = (): Partial<IPredictManagementContext> => {
    return {
      currency: currencySelector(organisation),
      organisation,
      financialConfig,
      refetchOrganisation,
      addErrorMessage: memoizedAddNormalisedErrorMessage,
      isFrenchAccounting: hasFrenchAccountingRules(financialConfig),
      googleSheetImportAllowed: hasAllowedGoogleSheetsImports(financialConfig)
    };
  };

  const productSetup = findProductSetupById(organisation, PREDICT_PRODUCT_ID);

  return (
    <PredictManagementContext.Provider value={getPredictContext()}>
      <PredictManagement productSetup={productSetup} />
      <UIPromiseTrackerLoader area={LOADER_AREAS.predictArea} />
    </PredictManagementContext.Provider>
  );
};
